import { useMemo, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { RISIKO_QUESTIONS } from "./data";
import type { KlasseKey, RiskQuestion, RiskSummary } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  highlightRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 760px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  highlightCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("2px", "solid", BRAND.pillarOrange),
    ...shorthands.borderRadius("12px"),
    paddingTop: "16px",
    paddingBottom: "18px",
    paddingLeft: "18px",
    paddingRight: "18px",
    boxShadow: "0 4px 14px rgba(230,106,44,0.08)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  highlightHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  highlightIcon: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.pillarOrange,
    color: BRAND.white,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  highlightTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.15rem",
    color: BRAND.ink,
    margin: 0,
  },
  highlightSub: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  plate: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: 800,
    fontSize: "1.6rem",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "16px",
    ...shorthands.border("2px", "solid", BRAND.ink),
    ...shorthands.borderRadius("6px"),
    letterSpacing: "0.06em",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "10px",
    minHeight: "32px",
  },
  plateBlue: {
    width: "10px",
    height: "100%",
    backgroundColor: BRAND.pillarBlue,
    alignSelf: "stretch",
    marginLeft: "-20px",
    marginRight: "10px",
    ...shorthands.borderRadius("4px", "0", "0", "4px"),
  },
  vinRow: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  vinLabel: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  vinValue: {
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
  },
  fahrerGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "14px",
    rowGap: "8px",
  },
  fahrerCell: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  fahrerLabel: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  fahrerValue: {
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  missing: {
    color: "#a99fa8",
    fontStyle: "italic",
  },
  group: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    overflow: "hidden",
  },
  header: {
    cursor: "pointer",
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: "#fbf7fc",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    width: "100%",
    appearance: "none",
    backgroundImage: "none",
    textAlign: "left",
    ...shorthands.border("0"),
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ":hover": {
      backgroundColor: "#f4eaf6",
    },
  },
  iconBubble: {
    width: "32px",
    height: "32px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  count: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  chevron: {
    transitionProperty: "transform",
    transitionDuration: "150ms",
    color: BRAND.muted,
    fontSize: "1.3rem",
    fontWeight: 700,
    width: "16px",
    textAlign: "center",
  },
  chevronOpen: {
    transform: "rotate(90deg)",
  },
  body: {
    paddingTop: "10px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "10px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  row: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    paddingTop: "6px",
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
  },
  rowLabel: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  rowValue: {
    fontSize: "0.95rem",
    color: BRAND.ink,
    whiteSpace: "pre-wrap",
  },
  notice: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
});

const SectionIcon = ({ section }: { section: string }) => {
  const props = {
    width: 18,
    height: 18,
    viewBox: "0 0 20 20",
    fill: "none",
    stroke: "currentColor" as const,
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (section.startsWith("1.")) {
    return (
      <svg {...props}>
        <rect x="2" y="6" width="16" height="8" rx="1.5" />
        <path d="M7 9h6" />
      </svg>
    );
  }
  if (section.startsWith("2.")) {
    return (
      <svg {...props}>
        <path d="M3 13l1.4-4a1.5 1.5 0 0 1 1.4-1h8.4a1.5 1.5 0 0 1 1.4 1l1.4 4" />
        <path d="M2 13h16v3a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H5v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
        <circle cx="6" cy="15" r="1.2" />
        <circle cx="14" cy="15" r="1.2" />
      </svg>
    );
  }
  if (section.startsWith("3.")) {
    return (
      <svg {...props}>
        <rect x="3" y="6" width="14" height="9" rx="1" />
        <path d="M3 10h14" />
        <path d="M7 4h2v2H7zM11 4h2v2h-2z" />
      </svg>
    );
  }
  if (section.startsWith("4.")) {
    return (
      <svg {...props}>
        <circle cx="10" cy="10" r="7" />
        <text x="10" y="13" textAnchor="middle" fontFamily="Arial" fontSize="9" fontWeight="800" fill="currentColor" stroke="none">€</text>
      </svg>
    );
  }
  if (section.startsWith("5.")) {
    return (
      <svg {...props}>
        <path d="M10 2l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V5z" />
        <path d="M7 10l2 2 4-4" />
      </svg>
    );
  }
  if (section.startsWith("6.")) {
    return (
      <svg {...props}>
        <circle cx="10" cy="7" r="3" />
        <path d="M3 17c0-3.5 3-6 7-6s7 2.5 7 6" />
      </svg>
    );
  }
  return null;
};

const groupBySection = (questions: RiskQuestion[]): { section: string; questions: RiskQuestion[] }[] => {
  const order: string[] = [];
  const map: Record<string, RiskQuestion[]> = {};
  for (const q of questions) {
    if (!map[q.section]) {
      map[q.section] = [];
      order.push(q.section);
    }
    map[q.section].push(q);
  }
  return order.map((s) => ({ section: s, questions: map[s] }));
};

type Props = {
  klasse: KlasseKey | null;
  risk: RiskSummary;
};

const RisikoinfoSection = ({ klasse, risk }: Props) => {
  const styles = useStyles();
  const groups = useMemo(() => {
    if (!klasse) return [];
    const list = RISIKO_QUESTIONS[klasse] ?? [];
    return groupBySection(list);
  }, [klasse]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const toggle = (section: string) =>
    setOpenMap((prev) => ({ ...prev, [section]: !prev[section] }));

  if (!klasse) {
    return (
      <div className={styles.notice}>
        Bitte zuerst die Tarifierungsart auswählen — die Risikoinformationen werden basierend
        auf der Fahrzeugklasse aus dem Risikoobjekt zusammengestellt.
      </div>
    );
  }

  const c = risk.characteristics;
  const v = (key: string) => (c[key] && String(c[key]).trim() !== "" ? String(c[key]) : null);
  const plate = v("licensePlate");
  const vin = v("vin");
  const fahrerFelder = [
    { label: "Fahrername", value: v("driverName") ?? v("policyholderName") ?? null },
    { label: "Fahrerkreis", value: v("driverCircle") ?? null },
    { label: "Fahreralter", value: v("driverAge") ?? null },
    { label: "Jährliche Fahrleistung", value: v("annualMileage") ?? null },
    { label: "Führerscheindatum", value: v("driverLicenseDate") ?? null },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.highlightRow}>
        <div className={styles.highlightCard}>
          <div className={styles.highlightHeader}>
            <span className={styles.highlightIcon}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="18" height="10" rx="2" />
                <path d="M7 11h8" />
              </svg>
            </span>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h3 className={styles.highlightTitle}>Kennzeichen & Fahrgestellnummer</h3>
              <span className={styles.highlightSub}>Identifikation des Fahrzeugs</span>
            </div>
          </div>
          <div className={styles.plate}>
            <span className={styles.plateBlue} aria-hidden="true" />
            <span>{plate ?? "— —"}</span>
          </div>
          <div className={styles.vinRow}>
            <span className={styles.vinLabel}>Fahrzeugidentnummer (VIN)</span>
            <span className={mergeClasses(styles.vinValue, !vin && styles.missing)}>
              {vin ?? "Nicht erfasst"}
            </span>
          </div>
        </div>

        <div className={styles.highlightCard} style={{ borderColor: BRAND.purple, boxShadow: "0 4px 14px rgba(77,32,81,0.1)" }}>
          <div className={styles.highlightHeader}>
            <span className={styles.highlightIcon} style={{ backgroundColor: BRAND.purple }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="8" r="3.4" />
                <path d="M3 19c0-3.8 3.4-6.6 8-6.6s8 2.8 8 6.6" />
              </svg>
            </span>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h3 className={styles.highlightTitle}>Fahrerdaten</h3>
              <span className={styles.highlightSub}>Hauptfahrer & Fahrerkreis</span>
            </div>
          </div>
          <div className={styles.fahrerGrid}>
            {fahrerFelder.map((f) => (
              <div key={f.label} className={styles.fahrerCell}>
                <span className={styles.fahrerLabel}>{f.label}</span>
                <span className={mergeClasses(styles.fahrerValue, !f.value && styles.missing)}>
                  {f.value ?? "Nicht erfasst"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.notice}>
        Die folgenden Abschnitte fassen alle erfassten Risikomerkmale zusammen. Sie werden
        ausschließlich angezeigt — Bearbeitung erfolgt im Risikoobjekt.
      </div>

      {groups.map((g) => {
        const open = !!openMap[g.section];
        const filled = g.questions.filter((q) => q.riskKey && c[q.riskKey] && String(c[q.riskKey]).trim() !== "").length;
        return (
          <div key={g.section} className={styles.group}>
            <button
              type="button"
              className={styles.header}
              onClick={() => toggle(g.section)}
              aria-expanded={open}
            >
              <span className={styles.iconBubble}>
                <SectionIcon section={g.section} />
              </span>
              <span className={styles.title}>{g.section}</span>
              <span className={styles.count}>
                {filled} / {g.questions.length} erfasst
              </span>
              <span className={mergeClasses(styles.chevron, open && styles.chevronOpen)}>›</span>
            </button>
            {open && (
              <div className={styles.body}>
                {g.questions.map((q, idx) => {
                  const hit = q.riskKey ? c[q.riskKey] : undefined;
                  const filled = !!(hit && String(hit).trim() !== "");
                  return (
                    <div key={`${q.section}-${idx}`} className={styles.row}>
                      <span className={styles.rowLabel}>{q.frage}</span>
                      <span className={mergeClasses(styles.rowValue, !filled && styles.missing)}>
                        {filled ? String(hit) : "Nicht erfasst"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RisikoinfoSection;
